
import * as React from "react";
import {
  ToastActionElement,
  ToastProps
} from "@/components/ui/toast";

const TOAST_LIMIT = 5;
const TOAST_REMOVE_DELAY = 1000000;

type ToasterToast = ToastProps & {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: ToastActionElement;
};

const actionTypes = {
  ADD_TOAST: "ADD_TOAST",
  UPDATE_TOAST: "UPDATE_TOAST",
  DISMISS_TOAST: "DISMISS_TOAST",
  REMOVE_TOAST: "REMOVE_TOAST",
} as const;

let count = 0;

function genId() {
  count = (count + 1) % Number.MAX_VALUE;
  return count.toString();
}

type ActionType = typeof actionTypes;

type Action =
  | {
      type: ActionType["ADD_TOAST"];
      toast: ToasterToast;
    }
  | {
      type: ActionType["UPDATE_TOAST"];
      toast: Partial<ToasterToast>;
    }
  | {
      type: ActionType["DISMISS_TOAST"];
      toastId?: string;
    }
  | {
      type: ActionType["REMOVE_TOAST"];
      toastId?: string;
    };

interface State {
  toasts: ToasterToast[];
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "ADD_TOAST":
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      };

    case "UPDATE_TOAST":
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t
        ),
      };

    case "DISMISS_TOAST": {
      const { toastId } = action;

      if (toastId) {
        toastTimeouts.forEach((_, key) => {
          if (key === toastId) {
            toastTimeouts.delete(key);
          }
        });

        return {
          ...state,
          toasts: state.toasts.map((t) =>
            t.id === toastId
              ? {
                  ...t,
                  open: false,
                }
              : t
          ),
        };
      }

      return {
        ...state,
        toasts: state.toasts.map((t) => ({
          ...t,
          open: false,
        })),
      };
    }
    case "REMOVE_TOAST": {
      const { toastId } = action;

      if (toastId) {
        return {
          ...state,
          toasts: state.toasts.filter((t) => t.id !== toastId),
        };
      }

      return {
        ...state,
        toasts: [],
      };
    }
  }
};

// Create a singleton state that can be accessed without using the hook
// This allows us to call toast() outside of React components
const TOAST_STATE = {
  toasts: [] as ToasterToast[],
  dispatch: null as null | React.Dispatch<Action>,
};

// Create a toast function that can be used outside of React components
const toast = ({ ...props }: Omit<ToasterToast, "id">) => {
  const id = genId();

  if (TOAST_STATE.dispatch) {
    TOAST_STATE.dispatch({
      type: "ADD_TOAST",
      toast: {
        ...props,
        id,
        open: true,
      },
    });
  }

  return id;
};

const useToast = () => {
  const [state, dispatch] = React.useReducer(reducer, {
    toasts: [],
  });

  // Update the singleton state when the hook is called
  React.useEffect(() => {
    TOAST_STATE.toasts = state.toasts;
    TOAST_STATE.dispatch = dispatch;
    return () => {
      TOAST_STATE.dispatch = null;
    };
  }, [state.toasts, dispatch]);

  React.useEffect(() => {
    state.toasts.forEach((toast) => {
      if (toast.open && toast.id && !toastTimeouts.has(toast.id)) {
        const timeoutId = setTimeout(() => {
          if (TOAST_STATE.dispatch) {
            TOAST_STATE.dispatch({ type: "DISMISS_TOAST", toastId: toast.id });
          }

          setTimeout(() => {
            if (TOAST_STATE.dispatch) {
              TOAST_STATE.dispatch({ type: "REMOVE_TOAST", toastId: toast.id });
            }
          }, TOAST_REMOVE_DELAY);
        }, toast.duration || 5000);

        toastTimeouts.set(toast.id, timeoutId);
      }
    });
  }, [state.toasts]);

  const toastInstance = React.useMemo(() => ({
    toasts: state.toasts,
    toast: ({ ...props }: Omit<ToasterToast, "id">) => {
      const id = genId();

      dispatch({
        type: "ADD_TOAST",
        toast: {
          ...props,
          id,
          open: true,
        },
      });

      return id;
    },
    dismiss: (toastId?: string) => {
      dispatch({ type: "DISMISS_TOAST", toastId });
    },
    update: (id: string, props: ToasterToast) => {
      dispatch({
        type: "UPDATE_TOAST",
        toast: { ...props, id },
      });
    },
  }), [state.toasts]);

  return toastInstance;
};

export { useToast, toast };
